import React from 'react';
import { Box, Grid, Paper, Typography, Rating } from '@mui/material';
import {
  Reviews as ReviewsIcon,
  PeopleAlt as PeopleIcon,
  Visibility as ViewsIcon,
  Share as ReferralsIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const METRIC_CONFIG = {
  maxTotalReviews: {
    icon: ReviewsIcon,
    label: 'Most Reviewed',
    statsPath: 'reviews.totalReviews',
    valueLabel: 'reviews'
  },
  maxTotalFollows: {
    icon: PeopleIcon,
    label: 'Most Followed',
    statsPath: 'follows.totalFollows',
    valueLabel: 'followers'
  },
  maxTotalPageViews: {
    icon: ViewsIcon,
    label: 'Most Viewed',
    statsPath: 'pageViews.totalPageViews',
    valueLabel: 'views'
  },
  maxTotalReferrals: {
    icon: ReferralsIcon,
    label: 'Most Referred',
    statsPath: 'referrals.totalReferrals',
    valueLabel: 'referrals'
  }
};

const getStatsValue = (stats, path) => {
  return path.split('.').reduce((obj, key) => obj?.[key], stats) || 0;
};

const TrustSignalCard = ({ business }) => {
  const navigate = useNavigate();
  const config = METRIC_CONFIG[business.metric];
  if (!config) return null;

  const Icon = config.icon;
  const value = getStatsValue(business.stats, config.statsPath);
  const businessDetails = business.businessDetail;

  const handleClick = () => {
    const formattedName = businessDetails.businessName.replace(/[\s,\.]+/g, '-').toLowerCase();
    navigate(`/business/${business.businessDetail._id}/${formattedName}`);
  };

  // 🆕 Generate structured data for this business
  const businessSchema = {
    "@type": "LocalBusiness",
    "name": businessDetails.businessName,
    "url": `https://www.revfer.com/business/${business.businessDetail._id}/${businessDetails.businessName.replace(/[\s,\.]+/g, '-').toLowerCase()}`,
    "category": businessDetails.category.join(', '),
    ...(businessDetails.avgSkillRating_all && {
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": businessDetails.avgSkillRating_all,
        "bestRating": 5,
        "worstRating": 1
      }
    }),
    ...(value && config.valueLabel === 'reviews' && {
      "review": {
        "@type": "Review",
        "reviewCount": value
      }
    })
  };

  return (
    <Grid item xs={12}>
      <Paper 
        variant="outlined" 
        sx={{ 
          p: 2, 
          border: 'none', 
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
          }
        }}
        onClick={handleClick}
        // 🆕 Add semantic HTML and microdata
        component="article"
        itemScope
        itemType="https://schema.org/LocalBusiness"
        role="button"
        tabIndex={0}
        aria-label={`View ${businessDetails.businessName} business details`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        {/* 🆕 Hidden structured data script for this business */}
        <script 
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              ...businessSchema
            })
          }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Icon sx={{ color: 'primary.main', mr: 1 }} aria-hidden="true" />
              <Typography 
                variant="body2" 
                color="primary" 
                sx={{ fontWeight: 500 }}
                component="span"
              >
                {config.label}: {value} {config.valueLabel}
              </Typography>
            </Box>
            
            <Typography 
              variant="subtitle1"
              component="h3" // 🆕 Proper heading hierarchy
              itemProp="name" // 🆕 Microdata
            >
              {businessDetails.businessName}
            </Typography>
            
            <Typography 
              variant="body2" 
              color="text.secondary"
              itemProp="category" // 🆕 Microdata
            >
              {businessDetails.category.join(' • ')}
            </Typography>
            
            <Box 
              sx={{ display: 'flex', alignItems: 'center', mt: 1 }}
              itemScope
              itemType="https://schema.org/AggregateRating" // 🆕 Rating microdata
            >
              <Rating 
                value={businessDetails.avgSkillRating_all || 0} 
                readOnly 
                size="small"
                aria-label={`Rating: ${businessDetails.avgSkillRating_all || 0} out of 5 stars`} // 🆕 Accessibility
              />
              <meta itemProp="ratingValue" content={businessDetails.avgSkillRating_all || 0} />
              <meta itemProp="bestRating" content="5" />
              <meta itemProp="worstRating" content="1" />
              
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ ml: 1 }}
                component="span"
              >
                {businessDetails.SkillValue_all ? 
                  `Smart Rating: ${Math.round(businessDetails.SkillValue_all * 10) / 10}` : 
                  'Smart Rating: N/A'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Grid>
  );
};

const TrustSignals = ({ topBusinesses }) => {
  if (!topBusinesses || !Array.isArray(topBusinesses)) {
    return null;
  }

  // 🔥 Filter out businesses that don't have businessDetail
  const validBusinesses = topBusinesses.filter(business => 
    business && 
    business.businessDetail && 
    business.businessDetail.businessName &&
    business.businessDetail._id
  );

  // Early return if no valid businesses after filtering
  if (validBusinesses.length === 0) {
    return null;
  }

  // 🆕 Generate ItemList structured data for the collection (using filtered businesses)
  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Top Trusted Businesses",
    "description": "Businesses with high trust signals including reviews, follows, views, and referrals",
    "numberOfItems": validBusinesses.length,
    "itemListElement": validBusinesses.map((business, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "LocalBusiness",
        "name": business.businessDetail.businessName,
        "url": `https://www.revfer.com/business/${business.businessDetail._id}/${business.businessDetail.businessName.replace(/[\s,\.]+/g, '-').toLowerCase()}`,
        "category": business.businessDetail.category ? business.businessDetail.category.join(', ') : '',
        ...(business.businessDetail.avgSkillRating_all && {
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": business.businessDetail.avgSkillRating_all,
            "bestRating": 5,
            "worstRating": 1
          }
        })
      }
    }))
  };

  return (
    <Box sx={{ mt: 4 }} component="section" aria-labelledby="trust-signals-heading">
      {/* 🆕 Hidden structured data for the business collection */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(itemListSchema)
        }}
      />

      <Typography 
        variant="h6" 
        gutterBottom
        component="h2" // 🆕 Proper heading hierarchy
        id="trust-signals-heading"
        itemProp="name"
      >
        Trust Signals
      </Typography>
      
      <Grid 
        container 
        spacing={2}
        component="div" // 🆕 Semantic container
        itemScope
        itemType="https://schema.org/ItemList" // 🆕 List microdata
        role="list" // 🆕 Accessibility
        aria-label="Top trusted businesses based on community engagement"
      >
        {validBusinesses.map(business => (
          <TrustSignalCard 
            key={business._id} 
            business={business}
          />
        ))}
      </Grid>
    </Box>
  );
};

export default TrustSignals;